/**
=========================================================
* Trust Cargo - filter controls
=========================================================

* The dropdowns and date boxes that sit next to the search on every list.
*
* They live here rather than being written per page so that every screen
* filters the same way and looks the same doing it, and so a page adds a
* filter in one line instead of twenty.
*
* All of them are plain inputs on purpose: they sit inside a single toolbar
* row, and MUI's full-size controls would push the tables below the fold -
* which is what this toolbar was built to stop.
*/

import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";

const boxSx = (active) => ({
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  px: 1.25,
  py: 0.5,
  borderRadius: 2,
  border: `1px solid ${active ? "rgba(201,162,39,0.55)" : "rgba(11,27,58,0.12)"}`,
  backgroundColor: active ? "rgba(201,162,39,0.10)" : "transparent",
  transition: "border-color 160ms ease, background-color 160ms ease",
});

const nativeSx = {
  border: "none",
  outline: "none",
  background: "transparent",
  fontFamily: "inherit",
  fontSize: 13.5,
  fontWeight: 600,
  color: "inherit",
  cursor: "pointer",
  maxWidth: 190,
};

/**
 * A labelled dropdown. `options` is [{value, label}]; the empty value is the
 * "any" case and is added automatically from `anyLabel`.
 */
export function FilterSelect({ label, value, onChange, options, anyLabel, icon }) {
  const active = value !== "" && value !== null && value !== undefined;

  return (
    <SuiBox sx={boxSx(active)}>
      {icon && (
        <Icon
          sx={{ fontSize: "17px !important", color: active ? "#C9A227" : "rgba(11,27,58,0.4)" }}
        >
          {icon}
        </Icon>
      )}

      <SuiTypography sx={{ fontSize: 12, opacity: 0.65, whiteSpace: "nowrap" }}>
        {label}
      </SuiTypography>

      <SuiBox
        component="select"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        sx={nativeSx}
      >
        <option value="">{anyLabel}</option>
        {options.map((o) => (
          <option key={String(o.value)} value={o.value}>
            {o.label}
          </option>
        ))}
      </SuiBox>

      {active && (
        <Icon
          onClick={() => onChange("")}
          sx={{
            cursor: "pointer",
            fontSize: "16px !important",
            color: "rgba(11,27,58,0.45)",
            "&:hover": { color: "#D32F2F" },
          }}
        >
          close
        </Icon>
      )}
    </SuiBox>
  );
}

FilterSelect.defaultProps = { anyLabel: "Any", icon: null, value: "" };

FilterSelect.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.node,
    })
  ).isRequired,
  anyLabel: PropTypes.string,
  icon: PropTypes.string,
};

/** A from/to pair over one date field. */
export function FilterDates({ from, to, onFrom, onTo, label }) {
  const active = Boolean(from || to);

  return (
    <SuiBox sx={boxSx(active)}>
      <Icon sx={{ fontSize: "17px !important", color: active ? "#C9A227" : "rgba(11,27,58,0.4)" }}>
        event
      </Icon>

      <SuiTypography sx={{ fontSize: 12, opacity: 0.65, whiteSpace: "nowrap" }}>
        {label}
      </SuiTypography>

      <SuiBox
        component="input"
        type="date"
        value={from}
        onChange={(e) => onFrom(e.target.value)}
        sx={nativeSx}
      />
      <SuiTypography sx={{ fontSize: 12, opacity: 0.5 }}>-</SuiTypography>
      <SuiBox
        component="input"
        type="date"
        value={to}
        onChange={(e) => onTo(e.target.value)}
        sx={nativeSx}
      />

      {active && (
        <Icon
          onClick={() => {
            onFrom("");
            onTo("");
          }}
          sx={{
            cursor: "pointer",
            fontSize: "16px !important",
            color: "rgba(11,27,58,0.45)",
            "&:hover": { color: "#D32F2F" },
          }}
        >
          close
        </Icon>
      )}
    </SuiBox>
  );
}

FilterDates.defaultProps = { label: "Date" };

FilterDates.propTypes = {
  from: PropTypes.string.isRequired,
  to: PropTypes.string.isRequired,
  onFrom: PropTypes.func.isRequired,
  onTo: PropTypes.func.isRequired,
  label: PropTypes.string,
};

/**
 * The distinct values a column actually holds, as select options.
 *
 * Building the list from the rows rather than a hardcoded one means the
 * filter can never offer a status that does not exist, or miss one the
 * office invented this morning.
 */
export function optionsFromRows(rows, key) {
  const seen = new Set();

  (rows || []).forEach((r) => {
    const v = r?.[key];
    if (v !== null && v !== undefined && String(v).trim() !== "") seen.add(String(v));
  });

  return [...seen].sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v }));
}

/** True when the row's value matches the chosen filter (or nothing is chosen). */
export function matches(rowValue, filterValue) {
  if (filterValue === "" || filterValue === null || filterValue === undefined) return true;
  return String(rowValue ?? "") === String(filterValue);
}

/** Inclusive date-range test against an ISO yyyy-mm-dd value. */
export function inDateRange(value, from, to) {
  const d = String(value || "");
  if (from && (!d || d < from)) return false;
  if (to && (!d || d > to)) return false;
  return true;
}
